import { useState } from 'react';
import { Play, ImageIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ExternalVideoPlayer } from './ExternalVideoPlayer';
import type { CommercialMedia, MediaPlacement } from './types';

export function CommercialMediaGallery({
  entries,
  fallbackCover,
  title,
}: {
  entries: CommercialMedia[];
  fallbackCover?: string | null;
  title?: string;
}) {
  const { t } = useTranslation('commerce');
  const [selected, setSelected] = useState<string | null>(null);
  const active = entries.find((entry) => `${entry.kind}:${entry.id}` === selected) ?? entries[0];
  if (!active) return null;
  return (
    <section aria-label={title || t('media.gallery')} className="min-w-0 space-y-3">
      {title && <h2 className="text-lg font-semibold">{title}</h2>}
      {active.kind === 'video' ? (
        <ExternalVideoPlayer
          key={`${active.id}:${active.url}`}
          video={active}
          fallbackCover={fallbackCover}
        />
      ) : (
        <img
          src={active.url}
          alt={active.title || title || t('media.photo')}
          className="aspect-square w-full rounded-xl border border-slate-200 bg-slate-50 object-contain dark:border-charcoal-700 dark:bg-charcoal-900"
        />
      )}
      {entries.length > 1 && (
        <div className="flex max-w-full gap-2 overflow-x-auto p-1" aria-label={t('media.choose')}>
          {entries.map((entry, index) => {
            const cover = entry.kind === 'image' ? entry.url : entry.cover_url || fallbackCover;
            return (
              <button
                key={`${entry.kind}:${entry.id}`}
                type="button"
                onClick={() => setSelected(`${entry.kind}:${entry.id}`)}
                aria-pressed={entry === active}
                aria-label={`${entry.kind === 'video' ? t('media.video') : t('media.photo')} ${index + 1} : ${entry.title}`}
                className={`relative flex size-16 shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--store-accent,theme(colors.lime.600))] dark:bg-charcoal-800 ${entry === active ? 'border-[var(--store-accent,theme(colors.lime.600))]' : 'border-slate-200 dark:border-charcoal-700'}`}
              >
                {cover ? (
                  <img src={cover} alt="" loading="lazy" className="h-full w-full object-contain" />
                ) : (
                  <ImageIcon className="size-5" />
                )}
                {entry.kind === 'video' && (
                  <span className="absolute rounded-full bg-charcoal-950 p-1 text-white">
                    <Play className="size-4" />
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </section>
  );
}

export function MediaAtPlacement({
  media,
  placement,
  fallbackCover,
}: {
  media?: CommercialMedia[] | null;
  placement: MediaPlacement;
  fallbackCover?: string | null;
}) {
  const { t } = useTranslation('commerce');
  const entries = media?.filter((entry) => entry.placement === placement) ?? [];
  if (!entries.length) return null;
  return (
    <div className="mx-auto my-6 w-full min-w-0 max-w-3xl">
      <CommercialMediaGallery
        entries={entries}
        fallbackCover={fallbackCover}
        title={placement === 'demonstration' ? t('media.demonstration') : undefined}
      />
    </div>
  );
}
